import { debounce } from 'lodash';
import { useMemo, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  IconBackgroundColor,
  IconBackgroundColorActive,
} from '@hivebrite/design-system/Icon';
import { useTheme, zIndices } from '@hivebrite/design-system/Theme';
import { Tooltip, POSITIONS } from '@hivebrite/design-system/Tooltip';

import { formatColor } from 'hb-builder/utils/colors';
import ColorPicker from 'hb-react/backoffice/components/DesignSystem/ColorPicker';
import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import ToolbarItem from 'hb-react/shared/components/DesignSystem/QuillEditor/Toolbar/components/ToolbarItem';
import { SESSION_STORAGE_KEY } from 'hb-react/shared/components/DesignSystem/QuillEditor/Toolbar/utils/getColorPresets';
import getPresetColors from 'hb-react/shared/components/DesignSystem/QuillEditor/Toolbar/utils/getPresetColors';
import { getBrandColors } from 'hb-react/shared/components/DesignSystem/QuillEditor/utils/colors';
import { addToStorage } from 'hb-react/shared/utils/sessionStorage';

const COLOR_PRESETS_LIMIT = 3;
const DEBOUNCE_CHANGE_COLOR = 150;

const BackgroundColor = ({
  isItemFocus,
  onItemClick,
  position = 'bottom',
}: {
  isItemFocus: boolean;
  onItemClick: () => void;
  position?: 'bottom' | 'top';
}) => {
  const theme = useTheme();
  const {
    quillRef,
    format,
    options: { toolbar: toolbarOptions } = {},
  } = useQuillEditorContext();

  const customColors =
    typeof toolbarOptions?.backgroundColor === 'object'
      ? toolbarOptions?.backgroundColor?.customColors
      : undefined;

  const brandColors = getBrandColors(customColors ?? theme.colors);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [tmpColor, setTmpColor] = useState(null);

  const defaultValue =
    quillRef.current &&
    window.getComputedStyle(quillRef.current.container).background;

  const initialValue = format?.background;
  const value =
    (Array.isArray(initialValue) ? initialValue[0] : initialValue) ||
    defaultValue;

  useEffect(() => {
    if (isItemFocus !== isPickerOpen) {
      setIsPickerOpen(isItemFocus);
    }
  }, [isItemFocus, isPickerOpen]);

  useEffect(() => {
    if (!isPickerOpen && tmpColor) {
      addToStorage({
        allowDuplicate: false,
        key: SESSION_STORAGE_KEY,
        value: formatColor({ color: tmpColor }),
        limit: COLOR_PRESETS_LIMIT,
      });

      setTmpColor(null);
    }
  }, [isPickerOpen, tmpColor]);

  const handleChangeColor = useMemo(
    () =>
      debounce((color) => {
        if (color) {
          setTmpColor(color);
        }

        quillRef.current?.format('background', color, 'user');
      }, DEBOUNCE_CHANGE_COLOR),
    [quillRef],
  );

  const handleClick = () => {
    setIsPickerOpen((prevState) => !prevState);
    onItemClick();
  };

  return (
    <Wrapper>
      <Tooltip
        initialPosition={POSITIONS.BOTTOM}
        childrenElement={__('Text background color')}
      >
        <ToolbarItem
          onClick={handleClick}
          ariaLabel={s__(
            'QuillEditorAccessibility|Change text background color',
          )}
        >
          <CurrentColorWrapper>
            {isPickerOpen ? (
              <IconBackgroundColorActive color={value || tmpColor} size={24} />
            ) : (
              <IconBackgroundColor color={value || tmpColor} size={24} />
            )}
          </CurrentColorWrapper>
        </ToolbarItem>
      </Tooltip>
      {isPickerOpen && (
        <ColorPickerWrapper $position={position}>
          <ColorPicker
            customOpacity
            defaultColor="black"
            id="background"
            onChange={handleChangeColor}
            presetColors={getPresetColors({
              type: 'BackgroundColor',
              theme,
              brandColors,
            })}
            value={value}
            width="100%"
          />
        </ColorPickerWrapper>
      )}
    </Wrapper>
  );
};

export default BackgroundColor;

const ColorPickerWrapper = styled(Box)<{ $position: 'bottom' | 'top' }>`
  left: 0px;
  position: absolute;
  top: ${({ $position }) => ($position === 'bottom' ? '30px' : 'auto')};
  bottom: ${({ $position }) => ($position === 'top' ? '30px' : 'auto')};
  user-select: none;
  z-index: ${zIndices.popover};
`;

const CurrentColorWrapper = styled(Box).attrs({
  display: 'block',
  height: '100%',
})``;

const Wrapper = styled(Box).attrs({
  width: 24,
  flexDirection: 'column',
})`
  position: relative;
`;
